import type React from 'react';
import type {
  DataGridCellAddress,
  DataGridCellRange,
  DataGridRowId,
} from './types';

/**
 * A range is stored as the two cells that made it, in whatever order they were
 * picked, so both axes are normalised before the comparison.
 */
export function isCellInRange(
  address: DataGridCellAddress,
  range: DataGridCellRange
): boolean {
  const minRow = Math.min(range.start.rowIndex, range.end.rowIndex);
  const maxRow = Math.max(range.start.rowIndex, range.end.rowIndex);
  const minColumn = Math.min(range.start.columnIndex, range.end.columnIndex);
  const maxColumn = Math.max(range.start.columnIndex, range.end.columnIndex);
  return (
    address.rowIndex >= minRow &&
    address.rowIndex <= maxRow &&
    address.columnIndex >= minColumn &&
    address.columnIndex <= maxColumn
  );
}

export function isCellSelected(
  address: DataGridCellAddress,
  ranges: DataGridCellRange[]
): boolean {
  return ranges.some((range) => isCellInRange(address, range));
}

export function getSelectionRange<TRow>({
  anchor,
  target,
  rows,
  getRowId,
}: {
  anchor: DataGridRowId;
  target: DataGridRowId;
  rows: TRow[];
  getRowId: (row: TRow) => DataGridRowId;
}): Set<DataGridRowId> {
  const rowIds = rows.map((row) => getRowId(row));
  const anchorIndex = rowIds.indexOf(anchor);
  const targetIndex = rowIds.indexOf(target);
  if (anchorIndex === -1 || targetIndex === -1) {
    return new Set([target]);
  }

  const start = Math.min(anchorIndex, targetIndex);
  const end = Math.max(anchorIndex, targetIndex);
  return new Set(rowIds.slice(start, end + 1));
}

export function isToggleSelectionEvent(
  event: MouseEvent | React.MouseEvent
): boolean {
  return event.metaKey || event.ctrlKey;
}

export function isRangeSelectionEvent(
  event: MouseEvent | React.MouseEvent
): boolean {
  return event.shiftKey;
}
